// Thin API wrapper for the YouTube AI Comment Replies feature. Every
// call is JWT-authenticated by the shared axios instance. Endpoints match
// /app/backend/youtube_replies_module.py. (Randy, Jul 2026)
import { api } from "./api";

export const ytApi = {
  callbackUri: () => api.get("/youtube/callback-uri").then((r) => r.data.callback_uri),

  setup: (siloId, slot, clientId, clientSecret) =>
    api.post("/youtube/setup", {
      silo_id: siloId,
      channel_slot: slot,
      google_client_id: clientId,
      google_client_secret: clientSecret,
    }).then((r) => r.data),

  authUrl: (siloId, slot) =>
    api.get(`/youtube/auth-url/${siloId}/${slot}`).then((r) => r.data),

  getIntegration: (siloId, slot) =>
    api.get(`/youtube/integration/${siloId}/${slot}`).then((r) => r.data.integration),

  // Iter160e (Randy) — cross-silo OAuth summary. Powers the auto-detect
  // banner at the top of the YT setup modals AND the smart LOAD-blocker
  // CTA on YTRE.
  listMyIntegrations: () =>
    api.get("/youtube/integrations/mine").then((r) => r.data.integrations || []),

  // Sep 15 2026 (Randy) — every YouTube hat this login has ever connected,
  // read from the append-only identity registry. `can_switch` means we hold
  // a live sign-in for it, so binding it to a silo needs no Google popup.
  myChannels: () =>
    api.get("/youtube/my-channels").then((r) => r.data.channels || []),

  // Bind a registered hat to (silo, slot) instantly. Replaces re-consenting
  // into the same slot, which used to destroy the hat being left behind.
  switchChannel: (siloId, slot, channelId) =>
    api.post("/youtube/switch-channel", {
      silo_id: siloId,
      channel_slot: slot,
      youtube_channel_id: channelId,
    }).then((r) => r.data),

  listChannelVideos: (siloId, slot) =>
    api.get(`/youtube/integration/${siloId}/${slot}/videos`).then((r) => r.data),

  patchIntegration: (siloId, slot, patch) =>
    api.patch(`/youtube/integration/${siloId}/${slot}`, patch).then((r) => r.data),

  disconnect: (siloId, slot) =>
    api.delete(`/youtube/integration/${siloId}/${slot}`).then((r) => r.data),

  togglePause: (siloId, slot) =>
    api.post(`/youtube/integration/${siloId}/${slot}/pause`).then((r) => r.data),

  poll: (siloId, slot) =>
    api.post(`/youtube/integration/${siloId}/${slot}/poll`).then((r) => r.data),

  // Jul 30, 2026 (Randy pt89c-xxxii) — Deeper user-initiated pull of
  // UNRESPONDED comments (up to 125 threads back, skips already-replied).
  // Only runs on user tap — burns YT quota + LLM budget.
  // Feb 2026 (Randy pt-yt-cursor-manual-reset) — accepts optional
  // `resetCursor` flag so the "Restart from newest" affordance can
  // force a fresh scan from the top of channel history (usually used
  // when the creator changed their monitored video list and wants to
  // catch stragglers on newly-monitored videos).
  backfill: (siloId, slot, opts = {}) =>
    api.post(`/youtube/integration/${siloId}/${slot}/backfill`, null, {
      params: {
        auto_post_eligible: !!opts.autoPost,
        reset_cursor: !!opts.resetCursor,
        // Oct 17, 2026 (Randy) — the pairing guard returns 409 unless the
        // creator has explicitly accepted drafting as a channel this silo
        // was not paired with. See PairingBanner.
        acknowledge_pairing: !!opts.acknowledgePairing,
      },
    }).then((r) => r.data),
  // Oct 17, 2026 (Randy) — the core-sample bar's data: channel start date,
  // newest comment, where the drill head has reached, per-year finds, and
  // whether a restart is recommended because new videos arrived.
  digMap: (siloId, slot) =>
    api.get(`/youtube/integration/${siloId}/${slot}/dig-map`).then((r) => r.data),
  // Feb 2026 (Randy pt-yt-post-all) — bulk-post every pending draft in
  // the inbox. Post-trickle rewrite (pt-yt-trickle): returns
  // `{ job_id, total_pending }` immediately and processing happens in
  // the background. Poll `postAllJobLatest` every 2s to render live
  // progress in the in-DOM banner. See YTRepliesConsoleCard.
  postAll: (siloId, slot) =>
    api.post(`/youtube/integration/${siloId}/${slot}/post-all`).then((r) => r.data),
  // Feb 2026 (Randy pt-yt-approve-all) — bulk-approve every SENT draft.
  // Sibling of postAll, but semantically different: it closes correction
  // windows early rather than creating new YouTube replies. Fast (no
  // YouTube API calls), so no background job needed.
  approveAll: (siloId, slot) =>
    api.post(`/youtube/integration/${siloId}/${slot}/approve-all`).then((r) => r.data),
  // Feb 2026 (Randy pt-yt-trickle) — latest Post ALL job for this
  // integration. Frontend polls this while a job is running so the
  // banner updates live, and also calls it on mount to reattach a
  // running job (page reload / tab-away recovery).
  postAllJobLatest: (siloId, slot) =>
    api.get(`/youtube/integration/${siloId}/${slot}/post-all-job/latest`).then((r) => r.data),
  postAllJobDismiss: (siloId, slot, jobId) =>
    api.post(`/youtube/integration/${siloId}/${slot}/post-all-job/${jobId}/dismiss`).then((r) => r.data),

  // Oct 2026 (LINK_LIBRARY_AUTOFIX_PROJECT.md · Phase A4) — re-draft the
  // comments currently listed in the Inbox. Replaces the old route of
  // "Clear inbox, then dig again" with writing the same group afresh, in
  // place. Background job; poll `redraftJobLatest` every 2s for the banner.
  redraftGroup: (siloId, slot, draftIds) =>
    api.post(`/youtube/integration/${siloId}/${slot}/redraft-group`, { draft_ids: draftIds }).then((r) => r.data),
  redraftJobLatest: (siloId, slot) =>
    api.get(`/youtube/integration/${siloId}/${slot}/redraft-job/latest`).then((r) => r.data),

  // Jul 30, 2026 (Randy pt89c-xxxiii) — Hard-delete all pending/sent/
  // skipped/rejected rows so Refresh / Pull-unresponded re-drafts fresh.
  clearInbox: (siloId, slot) =>
    api.post(`/youtube/integration/${siloId}/${slot}/clear-inbox`).then((r) => r.data),

  // Jul 30, 2026 (Randy pt89c-xxxiii) — Retro-fill is_question + video
  // title on any existing rows that lack them (idempotent).
  backfillMetadata: (siloId, slot) =>
    api.post(`/youtube/integration/${siloId}/${slot}/backfill-metadata`).then((r) => r.data),

  listDrafts: (siloId, slot, status = "pending") =>
    api.get(`/youtube/drafts/${siloId}/${slot}?status=${status}`).then((r) => r.data),

  approveDraft: (draftId) => api.post(`/youtube/drafts/${draftId}/approve`).then((r) => r.data),
  rejectDraft: (draftId) => api.post(`/youtube/drafts/${draftId}/reject`).then((r) => r.data),
  // Oct 2026 (Randy) — "has this been answered?" Asks YouTube about this
  // exact thread and, if he has already replied there, pulls his real words
  // into the card in place of the AI draft.
  checkAnswered: (draftId) =>
    api.post(`/youtube/drafts/${draftId}/check-answered`).then((r) => r.data),
  editDraft: (draftId, text, voiceOriginal) =>
    api.post(`/youtube/drafts/${draftId}/edit`, {
      text,
      ...(voiceOriginal === undefined || voiceOriginal === null ? {} : { voice_original: !!voiceOriginal }),
    }).then((r) => r.data),
  undoDraft: (draftId) => api.post(`/youtube/drafts/${draftId}/undo`).then((r) => r.data),
  redraft: (draftId, opts = {}) =>
    api.post(`/youtube/drafts/${draftId}/redraft`,
      // Feb 27 2026 (Randy pt-ytre-guide-this-reply) — one-shot owner
      // guidance from YTRedraftCapturePad's "Guide this reply" action.
      // Sent in the body so long strings don't blow the URL query limit.
      // Absent when the caller didn't stage guidance.
      opts.moldingHint ? { molding_hint: opts.moldingHint } : null,
      {
      // Feb 2026 (Randy pt-yt-length-override) — optional one-shot per-
      // draft length override. When present, backend uses this instead
      // of the integration's default Q/NQ length preset. Empty string
      // is valid ("let AI decide"). Undefined = no override sent.
      params: opts.lengthPreset !== undefined ? { length_preset: opts.lengthPreset } : undefined,
    }).then((r) => r.data),
  matchFiles: (draftId) => api.get(`/youtube/draft/${draftId}/match-files`).then((r) => r.data),
  appendQnaToFile: (draftId, docId) => api.post(`/youtube/drafts/${draftId}/append-qna`, { doc_id: docId }).then((r) => r.data),

  analytics: (siloId, slot) => api.get(`/youtube/analytics/${siloId}/${slot}`).then((r) => r.data),

  // Jul 30, 2026 (Randy pt89c-xxxi) — Posted-comment browser used by the
  // History pill. Time-range + filter + keyword search. Only queries on
  // user-initiated Load (not auto).
  postedHistory: (siloId, slot, { range = "3d", filter = "all", q = "" } = {}) =>
    api.get(`/youtube/drafts/${siloId}/${slot}/posted-history`, {
      params: { range, filter, ...(q ? { q } : {}) },
    }).then((r) => r.data),

  // ─── Build-as-you-go (Jun 2026, Randy) ───────────────────────────────
  // See /app/memory/FEATURE_BUILD_AS_YOU_GO_SPEC.md. The dig finds unanswered
  // comments on videos that are NOT in the silo yet — the ones the scrubber
  // skips in "selected" mode. It drafts nothing and spends no LLM money.
  digUnprocessed: (siloId, slot, opts = {}) =>
    api.post(`/youtube/integration/${siloId}/${slot}/dig-unprocessed`, null, {
      params: { reset_cursor: !!opts.resetCursor },
    }).then((r) => r.data),
  unprocessedQueue: (siloId, slot) =>
    api.get(`/youtube/integration/${siloId}/${slot}/unprocessed-queue`).then((r) => r.data),
  queueDismiss: (siloId, slot, body) =>
    api.post(`/youtube/integration/${siloId}/${slot}/queue-dismiss`, body).then((r) => r.data),
  queueToInbox: (siloId, slot, body) =>
    api.post(`/youtube/integration/${siloId}/${slot}/queue-to-inbox`, body).then((r) => r.data),
  // Cheap coverage-hole check: ledger + integration only, zero YouTube quota.
  coverageGaps: (siloId, slot) =>
    api.get(`/youtube/integration/${siloId}/${slot}/gaps`).then((r) => r.data),
};
